export const PASSCODE_MIN_LENGTH = 6;
export const PASSCODE_MAX_LENGTH = 32;
export const TOPIC_NAME_MAX_LENGTH = 50;
export const TOPIC_LOOKUP_MAX_LENGTH = 500;
export const SEARCH_MAX_LENGTH = 100;
export const PAGE_MAX = 10000;
export const ORDINARY_PAGE_SIZE_MAX = 100;
export const MEMBER_HISTORY_PAGE_SIZE_MAX = 500;

const CONTROL_OR_FORMAT_CHARACTER_PATTERN = /[\p{Cc}\p{Cf}\p{Zl}\p{Zp}]/u;

export function trimString(value: unknown): unknown {
  return typeof value === "string" ? value.trim() : value;
}

export function trimOptionalString(value: unknown): unknown {
  if (value === undefined || value === null) return value;
  return trimString(value);
}

/**
 * 兑换码是大小写敏感的字符串。这里只去除最外侧误输空白，
 * 不做大小写转换、不删除内部空白、不把数字转成数值。
 */
export function normalizePasscode(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

export function isValidPasscode(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length >= PASSCODE_MIN_LENGTH &&
    value.length <= PASSCODE_MAX_LENGTH &&
    /^[A-Za-z0-9]+$/.test(value)
  );
}

/** Preserve the exact title, including fullwidth characters and internal spaces. */
export function normalizeTopicName(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

export function containsControlOrFormatCharacters(value: unknown): boolean {
  return typeof value === "string" && CONTROL_OR_FORMAT_CHARACTER_PATTERN.test(value);
}

// Existing stored titles may predate the creation length policy.
export function isValidTopicLookupName(value: unknown): value is string {
  return typeof value === "string" && !containsControlOrFormatCharacters(value)
    && normalizeTopicName(value).length >= 1 && normalizeTopicName(value).length <= TOPIC_LOOKUP_MAX_LENGTH;
}

export function isValidTopicName(value: unknown): value is string {
  return isValidTopicLookupName(value) && normalizeTopicName(value).length <= TOPIC_NAME_MAX_LENGTH;
}

export function isValidSearch(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length <= SEARCH_MAX_LENGTH &&
    !containsControlOrFormatCharacters(value)
  );
}

export function isStrictHttpsUrl(value: unknown): value is string {
  if (typeof value !== "string" || !value) return false;
  try {
    const url = new URL(value);
    return (
      url.protocol === "https:" &&
      Boolean(url.hostname) &&
      !url.username &&
      !url.password &&
      !containsControlOrFormatCharacters(value)
    );
  } catch {
    return false;
  }
}

function isLocalUploadUrl(value: string): boolean {
  if (!value.startsWith("/uploads/") || containsControlOrFormatCharacters(value) || value.includes("\\")) return false;
  const rawPath = value.split(/[?#]/, 1)[0];
  const safePath = (path: string): boolean => path.startsWith("/uploads/")
    && path.length > "/uploads/".length && !containsControlOrFormatCharacters(path)
    && !path.includes("\\") && !path.split("/").some(segment => segment === "." || segment === "..");
  try {
    // One URL decode matches the static-upload path boundary; no synthetic origin.
    return safePath(rawPath) && safePath(decodeURIComponent(rawPath));
  } catch {
    return false;
  }
}

export function isAllowedQrImageUrl(value: unknown): value is string {
  return value === "" || (typeof value === "string" && isLocalUploadUrl(value)) || isStrictHttpsUrl(value);
}

export function isAllowedLinkUrl(value: unknown): value is string {
  return value === "" || isStrictHttpsUrl(value);
}

export function isSafeSingleLineText(value: unknown): value is string {
  return typeof value === "string" && !containsControlOrFormatCharacters(value);
}
